// Capture initial URL params BEFORE any other code can modify the URL
// This runs synchronously when the module loads
;(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const ref = urlParams.get('zyn')
  let box = urlParams.get('box')
  if (!box) {
    const boxMatch = /\/box\/([^/]+)/.exec(window.location.pathname)
    box = boxMatch?.[1] ?? null
  }
  window.__INITIAL_URL_PARAMS__ = { ref, box }
})()

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TonConnectUIProvider } from '@tonconnect/ui-react'
import WebApp from '@twa-dev/sdk'
import React, { type FC } from 'react'
import ReactDOM from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import { App } from './App'
import { loadLocaleData } from './Index.functions'
import { AppContextProvider } from './contexts/App.context'
import { OnboardingProvider } from './contexts/OnboardingContext'
import { ToastProvider } from './contexts/ToastContext'
import { ThemeProvider } from './hooks/useTheme'
import { detectBrowserLanguage } from './functions/detectBrowserLanguage'
import { initSentry } from './utils/sentry'
import { initAmplitude } from './utils/amplitude'
import './styles/reset.css'
import './styles/themes.css'
import './styles/bottomsheet.css'

// Initialize Telegram WebApp SDK
WebApp.ready()

// Initialize analytics as early as possible
initSentry()
initAmplitude()

const rootElement = document.getElementById('root')

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement)

  const queryClient = new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false } },
  })

  const bootstrapApplication = async (Component: FC) => {
    const language = detectBrowserLanguage()
    const messages = await loadLocaleData(language)

    root.render(
      <TonConnectUIProvider
        manifestUrl={`${process.env['APP_URL']}/tonconnect-manifest.json`}
        widgetRootId="tc-widget-root"
      >
        <QueryClientProvider client={queryClient}>
          <AppContextProvider language={language}>
            <ThemeProvider defaultTheme="light">
              <OnboardingProvider>
                <IntlProvider
                  messages={messages}
                  locale={language}
                  defaultLocale="ru"
                >
                  <ToastProvider>
                    <Component />
                  </ToastProvider>
                </IntlProvider>
              </OnboardingProvider>
            </ThemeProvider>
          </AppContextProvider>
        </QueryClientProvider>
      </TonConnectUIProvider>,
    )
  }

  bootstrapApplication(App)

  // Включаем HMR
  if (module.hot) {
    module.hot.accept('./App', async () => {
      const { App: NextApp } = await import('./App')
      bootstrapApplication(NextApp)
    })
  }
}
